import { EntityType, EntityInstance, EntityTypeRepository, EntityInstanceRepository } from '../domain/Entities';
import { query_dispatcher_get, sync_command_dispatcher, async_command_dispatcher, query_dispatcher_get_2 } from "@/api/client";
import { CommandDispatcher } from "@/modules/shared/domain/CommandDispatcher";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";
import { v4 as uuidv4 } from 'uuid';


function objectToDict(obj: any): Record<string, any> {
    const dict: Record<string, any> = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // Recursively convert nested object
                dict[key] = objectToDict(value);
            } else {
                dict[key] = value;
            }
        }
    }

    return dict;
}

export class EntityTypeApiRepository implements EntityTypeRepository {
    query_dispatcher: QueryDispatcher
    sync_command_dispatcher: CommandDispatcher
    async_command_dispatcher: CommandDispatcher

    constructor() {
        this.query_dispatcher = query_dispatcher_get
        this.sync_command_dispatcher = sync_command_dispatcher
        this.async_command_dispatcher = async_command_dispatcher
    }

    api_to_domain(entity_type) {
        // Ensure actions_catalog is always an array
        let actions_catalog = entity_type.actions_catalog;
        if (!Array.isArray(actions_catalog)) {
            actions_catalog = [];
        }
        let crud_slots = entity_type.crud_slots || {};
        return new EntityType(entity_type.uuid, entity_type.name, entity_type.schema, entity_type.user_id, entity_type.is_visible, entity_type.ui_config, entity_type.kind, entity_type.is_protected, actions_catalog, crud_slots, entity_type.version)
    }

    domain_to_api(entity_type: EntityType) {
        return {
            uuid: entity_type.uuid,
            name: entity_type.name,
            schema: entity_type.schema,
            user_id: entity_type.user_id,
            is_visible: entity_type.is_visible,
            ui_config: entity_type.ui_config,
            kind: entity_type.kind,
            is_protected: entity_type.is_protected,
            actions_catalog: Array.isArray(entity_type.actions_catalog) ? entity_type.actions_catalog : [],
            crud_slots: entity_type.crud_slots || {}
        }
    }


    async get_my_types(): Promise<EntityType[]> {
        let res = await this.query_dispatcher.dispatch('get.user.entity.types', {})
        return res.map((entity_type) => this.api_to_domain(entity_type))
    }

    async get_aux_workflow_editor_types(): Promise<EntityType[]> {
        let res = await this.query_dispatcher.dispatch('get.workflow.editor.entity.types', {})
        return res.map((entity_type) => this.api_to_domain(entity_type))
    }

    async get_UI_types(): Promise<EntityType[]> {
        let res = await this.query_dispatcher.dispatch('get.ui.entity.types', {})
        return res.map((entity_type) => this.api_to_domain(entity_type))
    }

    async get_type_by_id(type_id: string, explicit:boolean=false): Promise<EntityType> {
        let res = await this.query_dispatcher.dispatch('get.entity.type.by.uuid', {type_uuid: type_id, explicit: explicit})
        return this.api_to_domain(res)
    }

    async save(entity_type: EntityType): Promise<any> {
        try {
            let entity_data = this.domain_to_api(entity_type)
            let res = await this.sync_command_dispatcher.dispatch('save.entity.type', {entity_type: entity_data})
            return res['output']
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    create_empty_entity_type(): EntityType {
        let empty_schema = {
            type: 'object',
            properties: {},
            required: []
        }
        const uuid = uuidv4()
        return new EntityType(uuid, '', empty_schema, -1);
    }
}

export class EntityInstanceApiRepository implements EntityInstanceRepository {
    query_dispatcher: QueryDispatcher
    sync_command_dispatcher: CommandDispatcher
    async_command_dispatcher: CommandDispatcher
    constructor() {
        this.query_dispatcher = query_dispatcher_get_2
        this.sync_command_dispatcher = sync_command_dispatcher
        this.async_command_dispatcher = async_command_dispatcher
    }

    api_to_domain(entity_instance) {
        return new EntityInstance(entity_instance.uuid, entity_instance.entity_type_id, entity_instance.company_id, entity_instance.payload, entity_instance.created, entity_instance.download_date)
    }

    domain_to_api(entity_instance: EntityInstance) {
        return {
            uuid: entity_instance.uuid,
            entity_type_id: entity_instance.entity_type_id,
            company_id: entity_instance.company_id,
            payload: entity_instance.payload,
            created: entity_instance.created
        }
    }

    async get_instances_by_type(company_id: number, entity_type_id: string ,filters:Array<Object>): Promise<EntityInstance[]> {
        let res = await this.query_dispatcher.dispatch('get.instances.by.type', {company_id: company_id, entity_type_id: entity_type_id, filters:filters})
        return res.map((entity_instance) => this.api_to_domain(entity_instance))
    }

    async save(entity_instance: EntityInstance): Promise<EntityInstance> {
        try {
            let entity_data = this.domain_to_api(entity_instance)
            let res = await this.sync_command_dispatcher.dispatch('save.entity.instance', {entity_instance: entity_data})
            return res['output']
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    create_empty_entity_instance(company_id: number, entity_type: EntityType): EntityInstance {
        //No veig pq es infra aixo realment
        const uuid = uuidv4();
        // Use create_default_payload if configured, otherwise empty object
        const defaultPayload = entity_type.ui_config && entity_type.ui_config['create_default_payload'];
        let payload = defaultPayload ? { ...defaultPayload } : {};
        return new EntityInstance(uuid, entity_type.uuid, company_id, payload);
    }

    async get_instance_by_id(instance_id: string): Promise<EntityInstance> {
        try {
            let res = await this.query_dispatcher.dispatch('get.entity.instance', {instance_uuid: instance_id})
            return this.api_to_domain(res)
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async get_multiple_instances(instances_ids: string): Promise<EntityInstance> {
        try {
            let res = await this.query_dispatcher.dispatch('get.multiple.entity.instances', {instances_ids: instances_ids})
            let instances = res.map((entity_instance) => this.api_to_domain(entity_instance))
            return instances
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async update_multiple_instances(instances: EntityInstance[]): Promise<void> {
        try {
            let instances_data = instances.map((instance) => this.domain_to_api(instance))
            await this.sync_command_dispatcher.dispatch('update.multiple.entity.instances', {instances: instances_data})
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async delete_instances(instance_ids: string[]): Promise<void> {
        try {
            await this.sync_command_dispatcher.dispatch('delete.instances', {instances_ids: instance_ids})
        } catch (error) {
            console.log(error)
            throw error
        }
    }
    async download_instances(instance_ids: string[]): Promise<any> {
        try {
            let res=  await this.sync_command_dispatcher.dispatch('generate.excel.entity.instances', {entity_ids: instance_ids})
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async runWorkflowByParamsType(payload, e_type_id, company_id){
        try {
            let res = await this.sync_command_dispatcher.dispatch('run.workflow.by.params.type', {params: payload, params_type_uuid: e_type_id, company_id: company_id})
            console.log(res.output.result)
            return res.output.result
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    // New CRUD operations
    async filter_instances(
        entity_type_id: string,
        company_id: number,
        filters: Array<{match_key: string, value: string}> = [],
        limit: number | null = null,
        offset: number | null = null,
        sort_by: string | null = null,
        sort_desc: boolean = false
    ): Promise<any> {
        try {
            let params: any = {
                entity_type_id: entity_type_id,
                company_id: company_id,
                filters: filters,
                payload_form: true
            }
            if (limit !== null) {
                params.limit = limit
            }
            if (offset !== null) {
                params.offset = offset
            }
            if (sort_by !== null) {
                params.sort_by = sort_by
                params.sort_desc = sort_desc
            }
            let res = await this.query_dispatcher.dispatch('filter.instances', params)
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async create_or_update_instance(entity_instance_uuid: string, entity_type_uuid: string, payload: any, company_id: number, is_creation: boolean = false): Promise<any> {
        try {
            let res = await this.sync_command_dispatcher.dispatch('create.or.update.entity.instance', {
                entity_instance_uuid: entity_instance_uuid,
                entity_type_uuid: entity_type_uuid,
                payload: payload,
                company_id: company_id,
                is_creation: is_creation
            })
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async delete_entity_instances(instance_ids: string[], entity_type_uuid: string): Promise<any> {
        try {
            let res = await this.sync_command_dispatcher.dispatch('delete.entity.instances.v2', {
                instance_ids: instance_ids,
                entity_type_id: entity_type_uuid
            })
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async get_singleton_instance(entity_type_id: string, company_id: number): Promise<any> {
        try {
            let res = await this.query_dispatcher.dispatch('get.singleton.instance', {
                entity_type_id: entity_type_id,
                company_id: company_id
            })
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async get_entity_instance(uuid: string, entity_type_id: string, company_id: number): Promise<any> {
        try {
            let res = await this.query_dispatcher.dispatch('get.entity.instance', {
                uuid: uuid,
                entity_type_id: entity_type_id,
                company_id: company_id
            })
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    // Firma la URL del PDF de un anexo (p.ej. la 'suma') para previsualizarlo.
    // Devuelve { signed_url } (query -> respuesta directa, sin envoltura output).
    async sign_annex_pdf(annex_uuid: string): Promise<any> {
        try {
            let res = await this.query_dispatcher.dispatch('annexes.sign_pdf', {
                annex_uuid: annex_uuid
            })
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }
}


export class MemoryEntityTypeRepository implements EntityTypeRepository {
    types: Array<EntityType> = []

    constructor(data) {
        this.types = data
    }

    async get_my_types(): Promise<EntityType[]> {
        return this.types
    }

    async get_aux_workflow_editor_types(): Promise<EntityType[]> {
        return []
    }

    async get_UI_types(): Promise<EntityType[]> {
        return this.types
    }

    async get_type_by_id(type_id: string): Promise<EntityType> {
        return this.types.find(t => t.uuid == type_id)
    }

    async save(entity_type: EntityType): Promise<any> {
        return entity_type
    }

    create_empty_entity_type(): EntityType {
        return new EntityType(uuidv4(), '', {type: 'object', properties: {}, required: []}, -1);
    }
}